import { Fragment, memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BarChart3 } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useSimulationDetail, useSimulationResult } from "@/hooks/api/useSimulations";
import type { SimulationResultItem } from "@/services/simulations.service";

// ─── Constants ────────────────────────────────────────────────────────────────

const MESES_SHORT = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];
const MESES_LONG = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 11 }, (_, i) => CURRENT_YEAR + i);

// ─── Types ────────────────────────────────────────────────────────────────────

interface ColDef { year: number; month: number; showYear: boolean }
// key = `${year}|${month}|${catId}|${isMain ? 1 : 0}`
type ResultMap = Map<string, SimulationResultItem[]>;

// ─── Utilities ────────────────────────────────────────────────────────────────

function cellKey(year: number, month: number, catId: number, isMain: boolean): string {
  return `${year}|${month}|${catId}|${isMain ? 1 : 0}`;
}

function fmt(n: number) { return n.toLocaleString("pt-BR"); }

function generateColumns(sy: number, sm: number, ey: number, em: number): ColDef[] {
  const cols: ColDef[] = [];
  let y = sy, m = sm;
  while (y < ey || (y === ey && m <= em)) {
    cols.push({ year: y, month: m, showYear: cols.length === 0 || cols[cols.length - 1].year !== y });
    m++; if (m > 12) { m = 1; y++; }
  }
  return cols;
}

// ─── Cell component ───────────────────────────────────────────────────────────

interface ResultCellProps {
  items: SimulationResultItem[] | undefined;
  isMain: boolean;
  onOpen: () => void;
}

const ResultCell = memo(function ResultCell({ items, isMain, onOpen }: ResultCellProps) {
  const relevant = useMemo(() => {
    if (!items) return [];
    return isMain
      ? items.filter(i => i.main_product_quantity > 0)
      : items.filter(i => i.secondary_product_quantity > 0);
  }, [items, isMain]);

  const total = relevant.reduce(
    (s, i) => s + (isMain ? i.main_product_quantity : i.secondary_product_quantity),
    0,
  );

  if (relevant.length === 0) {
    return (
      <div
        className="h-[80px] rounded-xl border border-dashed border-[#C2D1D2] bg-[#F7FAFB] flex items-center justify-center"
      >
        <span className="text-xs text-[#BAC9CA]">—</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      className={`h-[80px] rounded-xl border p-2.5 flex flex-col items-center justify-center gap-1 w-full transition-opacity hover:opacity-80 ${
        isMain
          ? "bg-[#E0F2F1] border-[#88C0B2]"
          : "bg-[#F5F5F5] border-[#C8D5D6]"
      }`}
    >
      <span className={`text-[11px] font-semibold leading-none ${isMain ? "text-[#135045]" : "text-[#3B6E68]"}`}>
        {relevant.length} {relevant.length === 1 ? "livro" : "livros"}
      </span>
      <span className={`text-[10px] leading-none ${isMain ? "text-[#3B6E68]" : "text-[#6B797A]"}`}>
        {fmt(total)} un.
      </span>
    </button>
  );
});

// ─── Book row in Sheet ────────────────────────────────────────────────────────

function BookRow({
  item,
  isMain,
  totalQty,
}: {
  item: SimulationResultItem;
  isMain: boolean;
  totalQty: number;
}) {
  const qty = isMain ? item.main_product_quantity : item.secondary_product_quantity;
  const pct = totalQty > 0 ? Math.round((qty / totalQty) * 100) : 0;
  return (
    <div className="py-3 border-b border-[#EDF4F5] last:border-0">
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#151C1D] leading-snug">{item.book_title}</p>
          <p className="text-[11px] text-[#6B797A] mt-0.5">
            ISBN: {item.book_isbn} · SK: {item.sk_code}
          </p>
        </div>
        <span className="shrink-0 text-sm font-semibold text-[#151C1D]">
          {fmt(qty)} un.
        </span>
      </div>
      <div className="text-[11px] text-[#6B797A]">
        {pct}% do total
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function SimulationResults() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const simulationId = Number(id);

  const { data: simulation, isLoading: loadingSim } = useSimulationDetail(simulationId);
  const { data: result, isLoading: loadingResult, isError, error } = useSimulationResult(simulationId);

  // ── Range ────────────────────────────────────────────────────────────────────
  const [startMonth, setStartMonth] = useState(1);
  const [startYear, setStartYear]   = useState(CURRENT_YEAR);
  const [endMonth, setEndMonth]     = useState(12);
  const [endYear, setEndYear]       = useState(CURRENT_YEAR);
  const [rangeInitialized, setRangeInitialized] = useState(false);

  useEffect(() => {
    if (rangeInitialized || !result || result.years.length === 0) return;
    let minYear = Infinity, minMonth = 13, maxYear = -Infinity, maxMonth = 0;
    for (const yd of result.years) {
      for (const md of yd.months) {
        if (yd.year < minYear || (yd.year === minYear && md.month < minMonth)) {
          minYear = yd.year; minMonth = md.month;
        }
        if (yd.year > maxYear || (yd.year === maxYear && md.month > maxMonth)) {
          maxYear = yd.year; maxMonth = md.month;
        }
      }
    }
    if (Number.isFinite(minYear)) { setStartYear(minYear); setStartMonth(minMonth); }
    if (Number.isFinite(maxYear)) { setEndYear(maxYear);   setEndMonth(maxMonth); }
    setRangeInitialized(true);
  }, [result, rangeInitialized]);

  // ── Effective end (clamp) ────────────────────────────────────────────────────
  const effEndYear  = endYear  < startYear ? startYear : endYear;
  const effEndMonth = effEndYear === startYear && endMonth < startMonth ? startMonth : endMonth;

  // ── Columns ──────────────────────────────────────────────────────────────────
  const columns = useMemo(
    () => generateColumns(startYear, startMonth, effEndYear, effEndMonth),
    [startYear, startMonth, effEndYear, effEndMonth],
  );

  // ── Categories ───────────────────────────────────────────────────────────────
  const categories = useMemo(() => {
    if (!result) return [];
    const seen = new Map<number, string>();
    for (const yd of result.years) {
      for (const md of yd.months) {
        for (const cat of md.categories) {
          if (!seen.has(cat.category_id)) seen.set(cat.category_id, cat.category_code);
        }
        if (seen.size > 0) break;
      }
      if (seen.size > 0) break;
    }
    return [...seen.entries()]
      .map(([id, code]) => ({ id, code }))
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [result]);

  // ── Result map ───────────────────────────────────────────────────────────────
  // key includes isMain to split Primary/Secondary items per cell
  const resultMap = useMemo<ResultMap>(() => {
    const map = new Map<string, SimulationResultItem[]>();
    if (!result) return map;
    for (const yd of result.years) {
      for (const md of yd.months) {
        for (const cat of md.categories) {
          if (!cat.items.length) continue;
          const mainItems = cat.items.filter(i => i.main_product_quantity > 0);
          const secItems  = cat.items.filter(i => i.secondary_product_quantity > 0);
          if (mainItems.length) map.set(cellKey(yd.year, md.month, cat.category_id, true), mainItems);
          if (secItems.length)  map.set(cellKey(yd.year, md.month, cat.category_id, false), secItems);
        }
      }
    }
    return map;
  }, [result]);

  // ── Sheet ────────────────────────────────────────────────────────────────────
  const [sheetOpen, setSheetOpen]   = useState(false);
  const [sheetTitle, setSheetTitle] = useState("");
  const [sheetItems, setSheetItems] = useState<SimulationResultItem[]>([]);
  const [sheetIsMain, setSheetIsMain] = useState(true);

  const openSheet = useCallback((title: string, items: SimulationResultItem[], isMain: boolean) => {
    setSheetTitle(title);
    setSheetItems(items);
    setSheetIsMain(isMain);
    setSheetOpen(true);
  }, []);

  const isLoading = loadingSim || loadingResult;

  return (
    <div className="flex h-screen bg-white">
      <Navigation />
      <main className="flex-1 flex flex-col overflow-hidden">

        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <div className="sticky top-0 z-50 border-b border-[#BAC9CA] bg-white">
          <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-4">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/simulacoes/${id}`)}
                className="w-9 h-9 rounded-full hover:bg-[#0000000D]"
              >
                <ArrowLeft className="w-5 h-5 text-[#151C1D]" />
              </Button>
              <div>
                <h1 className="text-[22px] font-medium text-[#151C1D] leading-tight">
                  Resultados da Simulação
                </h1>
                {simulation?.name && (
                  <p className="text-xs text-[#3B494A]">{simulation.name}</p>
                )}
              </div>
            </div>

            {/* Range selector — same native <select> style as MaterialityCalendar */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-[#3B494A] whitespace-nowrap">De</span>
                <select
                  value={startMonth}
                  onChange={(e) => setStartMonth(Number(e.target.value))}
                  className="h-9 rounded-lg border border-[#BAC9CA] bg-[#EDF4F5] px-2 text-sm text-[#151C1D] focus:outline-none focus:ring-1 focus:ring-[#00646B]"
                >
                  {MESES_SHORT.map((m, i) => (
                    <option key={i + 1} value={i + 1}>{m}</option>
                  ))}
                </select>
                <select
                  value={startYear}
                  onChange={(e) => setStartYear(Number(e.target.value))}
                  className="h-9 rounded-lg border border-[#BAC9CA] bg-[#EDF4F5] px-2 text-sm text-[#151C1D] focus:outline-none focus:ring-1 focus:ring-[#00646B]"
                >
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              <span className="text-[#3B494A] text-sm">→</span>

              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-[#3B494A] whitespace-nowrap">Até</span>
                <select
                  value={endMonth}
                  onChange={(e) => setEndMonth(Number(e.target.value))}
                  className="h-9 rounded-lg border border-[#BAC9CA] bg-[#EDF4F5] px-2 text-sm text-[#151C1D] focus:outline-none focus:ring-1 focus:ring-[#00646B]"
                >
                  {MESES_SHORT.map((m, i) => (
                    <option key={i + 1} value={i + 1}>{m}</option>
                  ))}
                </select>
                <select
                  value={endYear}
                  onChange={(e) => setEndYear(Number(e.target.value))}
                  className="h-9 rounded-lg border border-[#BAC9CA] bg-[#EDF4F5] px-2 text-sm text-[#151C1D] focus:outline-none focus:ring-1 focus:ring-[#00646B]"
                >
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              {columns.length > 0 && (
                <span className="text-xs text-[#6B797A] whitespace-nowrap">
                  {columns.length} {columns.length === 1 ? "mês" : "meses"}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── Grid ───────────────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-40 text-sm text-[#3B494A]">
              Carregando resultados…
            </div>
          ) : isError || !result ? (
            <div className="flex items-center justify-center h-40">
              <div className="rounded-2xl border border-[#F5C2C7] bg-[#FFF5F5] px-5 py-4 text-sm text-[#93000A]">
                {(error as Error)?.message ?? "Erro ao carregar resultados."}
              </div>
            </div>
          ) : categories.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 gap-2 text-[#3B494A]">
              <BarChart3 className="w-10 h-10 text-[#BAC9CA]" />
              <span className="text-sm">Nenhum resultado disponível.</span>
            </div>
          ) : (
            <div
              className="px-5 py-5"
              style={{
                display: "grid",
                gridTemplateColumns: `152px repeat(${columns.length}, 172px)`,
                columnGap: "6px",
                rowGap: "4px",
                minWidth: "max-content",
              }}
            >
              {/* ── Header row ── */}
              <div className="sticky top-0 left-0 z-50 h-12 bg-white rounded-xl" />
              {columns.map(({ year, month, showYear }) => (
                <div
                  key={`h-${year}-${month}`}
                  className="sticky top-0 z-40 h-12 flex flex-col items-center justify-center rounded-xl bg-[#EDF4F5] border border-[#BAC9CA]"
                >
                  <span className="text-xs font-medium text-[#151C1D] leading-none">
                    {MESES_SHORT[month - 1]}
                  </span>
                  {showYear && (
                    <span className="text-[9px] font-semibold text-[#00646B] mt-0.5 leading-none">
                      {year}
                    </span>
                  )}
                </div>
              ))}

              {/* ── Data rows ── */}
              {categories.map((cat, catIdx) => (
                <Fragment key={cat.id}>
                  {/* Divider between categories */}
                  {catIdx > 0 && (
                    <div
                      style={{
                        gridColumn: "1 / -1",
                        height: "1px",
                        background: "#D6E5E6",
                        margin: "2px 0",
                      }}
                    />
                  )}

                  {/* Primária label */}
                  <div
                    className="sticky left-0 z-30 h-[80px] rounded-xl border border-[#BAC9CA] flex flex-col items-center justify-center px-2"
                    style={{ background: "linear-gradient(135deg,#E0F2F1 0%,#F0FAF9 100%)" }}
                  >
                    <span className="text-sm font-bold text-[#135045]">{cat.code}</span>
                    <span className="text-[10px] text-[#3B494A] mt-0.5 uppercase tracking-wide font-medium">
                      Primária
                    </span>
                  </div>
                  {/* Primária cells */}
                  {columns.map(({ year, month }) => {
                    const key = cellKey(year, month, cat.id, true);
                    const items = resultMap.get(key);
                    return (
                      <ResultCell
                        key={key}
                        items={items}
                        isMain={true}
                        onOpen={() =>
                          openSheet(
                            `${cat.code} · Primária · ${MESES_LONG[month - 1]} ${year}`,
                            items ?? [],
                            true,
                          )
                        }
                      />
                    );
                  })}

                  {/* Secundária label */}
                  <div
                    className="sticky left-0 z-30 h-[80px] rounded-xl border border-[#BAC9CA] flex flex-col items-center justify-center px-2"
                    style={{ background: "linear-gradient(135deg,#F5FAFB 0%,#FFFFFF 100%)" }}
                  >
                    <span className="text-sm font-bold text-[#3B6E68]">{cat.code}</span>
                    <span className="text-[10px] text-[#6B797A] mt-0.5 uppercase tracking-wide font-medium">
                      Secundária
                    </span>
                  </div>
                  {/* Secundária cells */}
                  {columns.map(({ year, month }) => {
                    const key = cellKey(year, month, cat.id, false);
                    const items = resultMap.get(key);
                    return (
                      <ResultCell
                        key={key}
                        items={items}
                        isMain={false}
                        onOpen={() =>
                          openSheet(
                            `${cat.code} · Secundária · ${MESES_LONG[month - 1]} ${year}`,
                            items ?? [],
                            false,
                          )
                        }
                      />
                    );
                  })}
                </Fragment>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* ── Sheet ─────────────────────────────────────────────────────────────── */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-[500px] p-0 flex flex-col">
          <SheetHeader className="px-5 py-4 border-b border-[#BAC9CA] shrink-0">
            <SheetTitle className="text-sm font-semibold text-[#151C1D] leading-snug pr-8">
              {sheetTitle}
            </SheetTitle>
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="bg-[#EDF4F5] text-[#00646B] rounded-full px-2.5 py-1 text-xs font-medium">
                {sheetItems.length} {sheetItems.length === 1 ? "livro" : "livros"}
              </span>
              <span className="bg-[#F3F6F7] text-[#3B494A] rounded-full px-2.5 py-1 text-xs">
                {fmt(
                  sheetItems.reduce(
                    (s, i) => s + (sheetIsMain ? i.main_product_quantity : i.secondary_product_quantity),
                    0,
                  ),
                )}{" "}
                un.
              </span>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-5 py-1">
            {sheetItems.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-sm text-[#6B797A]">
                Nenhum livro neste slot.
              </div>
            ) : (() => {
              const totalQty = sheetItems.reduce(
                (s, i) => s + (sheetIsMain ? i.main_product_quantity : i.secondary_product_quantity),
                0,
              );
              return sheetItems
                .slice()
                .sort((a, b) => {
                  const qa = sheetIsMain ? a.main_product_quantity : a.secondary_product_quantity;
                  const qb = sheetIsMain ? b.main_product_quantity : b.secondary_product_quantity;
                  return qb - qa;
                })
                .map((item) => (
                  <BookRow key={item.id} item={item} isMain={sheetIsMain} totalQty={totalQty} />
                ));
            })()}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
